import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  TrendingUp, BarChart3, Eye, Bookmark, FolderKanban, Search,
  Loader2, ArrowRight, Calendar, Sparkles
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const TrendsDashboard = () => {
  const { user } = useAuth();
  
  const [trends, setTrends] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');

  useEffect(() => {
    fetchTrends();
  }, []);

  useEffect(() => {
    fetchInsights();
  }, [period]);

  const fetchTrends = async () => {
    try {
      const response = await axios.get(`${API}/trends/dashboard`, { withCredentials: true });
      setTrends(response.data);
    } catch (error) {
      console.error('Failed to fetch trends:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInsights = async () => {
    try {
      const response = await axios.get(`${API}/trends/insights?period=${period}`, { withCredentials: true });
      setInsights(response.data);
    } catch (error) {
      console.error('Failed to fetch insights:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      <main className="flex-1 max-w-[1600px] mx-auto px-6 py-12 pt-28 w-full">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12">
          <div>
            <p className="caption-text text-muted-foreground mb-2">Analytics</p>
            <h1 className="heading-display text-4xl font-medium flex items-center gap-3">
              <TrendingUp className="w-10 h-10 text-primary" />
              Material Trends
            </h1>
            <p className="text-muted-foreground mt-2">
              Discover what's trending based on platform activity
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Link to="/trends/my-materials">
              <Button variant="outline">
                <Sparkles className="w-4 h-4 mr-2" />
                I Miei Materiali
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                  <SelectItem value="90d">Last 90 days</SelectItem>
                  <SelectItem value="12m">Last 12 months</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Insights Summary */}
        {insights && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-secondary/30 p-6">
              <div className="flex items-center gap-3 mb-2">
                <Search className="w-5 h-5 text-[var(--gold)]" />
                <span className="text-sm text-muted-foreground">Visual Searches</span>
              </div>
              <p className="text-3xl font-semibold">{insights.insights.visual_searches}</p>
            </div>
            <div className="bg-secondary/30 p-6">
              <div className="flex items-center gap-3 mb-2">
                <Bookmark className="w-5 h-5 text-green-500" />
                <span className="text-sm text-muted-foreground">Specifications</span>
              </div>
              <p className="text-3xl font-semibold">{insights.insights.specifications_generated}</p>
            </div>
            <div className="bg-secondary/30 p-6">
              <div className="flex items-center gap-3 mb-2">
                <FolderKanban className="w-5 h-5 text-purple-500" />
                <span className="text-sm text-muted-foreground">Advisor Requests</span>
              </div>
              <p className="text-3xl font-semibold">{insights.insights.advisor_requests}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Top Trending Materials */}
          <div className="lg:col-span-2">
            <h2 className="text-xl font-medium mb-6 flex items-center gap-2">
              <BarChart3 className="w-5 h-5" /> Top Trending Materials
            </h2>
            
            {trends?.top_trending?.length > 0 ? (
              <div className="space-y-4">
                {trends.top_trending.map((material, idx) => (
                  <Link
                    key={material.material_id}
                    to={`/materials/${material.material_id}`}
                    className="flex items-center gap-4 p-4 bg-secondary/30 hover:bg-secondary/50 transition-colors"
                  >
                    {/* Rank */}
                    <div className={`w-8 h-8 flex items-center justify-center font-semibold ${
                      idx < 3 ? 'bg-primary text-primary-foreground' : 'bg-secondary'
                    }`}>
                      {idx + 1}
                    </div>
                    
                    {/* Thumbnail */}
                    {material.thumbnail && (
                      <img 
                        src={material.thumbnail}
                        alt={material.material_name}
                        className="w-16 h-16 object-cover"
                      />
                    )}
                    
                    {/* Info */}
                    <div className="flex-1">
                      <h3 className="font-medium">{material.material_name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {material.stone_type} • {material.color}
                      </p>
                    </div>
                    
                    {/* Score & Signals */}
                    <div className="text-right">
                      <p className="font-semibold text-primary">{material.trend_score} pts</p>
                      <div className="flex gap-2 text-xs text-muted-foreground">
                        <span title="Board additions">📋 {material.signals.boards || 0}</span>
                        <span title="Searches">🔍 {material.signals.searches || 0}</span>
                        <span title="Specs">📄 {material.signals.saves || 0}</span>
                      </div>
                    </div>
                    
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Not enough data yet to show trends</p>
                <p className="text-sm mt-2">Start using the platform to generate trend insights</p>
              </div>
            )}
          </div>

          {/* Side Panels */}
          <div className="space-y-8">
            {/* Trending Categories */}
            <div>
              <h3 className="text-lg font-medium mb-4">Trending Categories</h3>
              {trends?.trending_categories?.length > 0 ? (
                <div className="space-y-2">
                  {trends.trending_categories.slice(0, 6).map((cat, idx) => (
                    <div key={cat.category} className="flex items-center gap-3">
                      <div className="w-full bg-secondary h-8 relative">
                        <div 
                          className="absolute left-0 top-0 h-full bg-primary/20"
                          style={{ width: `${Math.min(100, (cat.score / (trends.trending_categories[0]?.score || 1)) * 100)}%` }}
                        />
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium z-10">
                          {cat.category}
                        </span>
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground z-10">
                          {cat.score}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No category data yet</p>
              )}
            </div>

            {/* Trending Colors */}
            <div>
              <h3 className="text-lg font-medium mb-4">Trending Colors</h3>
              {trends?.trending_colors?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {trends.trending_colors.map((color, idx) => (
                    <div 
                      key={color.color}
                      className="px-3 py-1 bg-secondary text-sm"
                    >
                      {color.color} <span className="text-muted-foreground">({color.score})</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No color data yet</p>
              )}
            </div>

            {/* Info */}
            <div className="p-4 bg-[var(--gold)]/10 text-sm">
              <h4 className="font-medium text-[var(--gold)] mb-2">How trends are calculated</h4>
              <p className="text-white/70">
                Trends are based on platform activity: board additions, visual search results, 
                specification downloads, and advisor requests. Higher weights are given to 
                actions that indicate stronger interest.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TrendsDashboard;
